// API-key policy, shared by the backend (generation, `_auth`, the sweep) and the
// frontend (the create form's bounds, the status column). Canonical here so the two
// cannot drift — a UI offering 120 days against a backend capping at 90 would
// produce a validation error the user could not have predicted.

/** Policy windows are expressed in hours and days; every comparison is in milliseconds. */
export const MS_PER_HOUR = 60 * 60 * 1000
export const MS_PER_DAY = 24 * MS_PER_HOUR

/** 256 bits — past the point where guessing, rather than leakage, is the attack. */
export const API_KEY_BYTES = 32

/**
 * Length of the base62 random segment: ceil(256 / log2(62)) = 43 characters.
 *
 * Rendering a number drops its leading zeros, so a value small enough to fit in
 * fewer digits would otherwise produce a shorter key — rare, but real, and it
 * would make key length a variable the format cannot promise. Generation pads to
 * this width instead, so every plaintext is the same shape and no consumer has to
 * treat a short one as anything other than a key.
 */
export const API_KEY_RANDOM_CHARS = 43

/** Lifetime applied when the requester names none. */
export const API_KEY_DEFAULT_EXPIRY_DAYS = 30

/**
 * Longest lifetime a person may request. Requests above it are rejected rather than
 * clamped, so a caller cannot believe a key lives four times longer than it does.
 * The system identity's key is exempt and is not created through that path.
 */
export const API_KEY_MAX_EXPIRY_DAYS = 90

/**
 * How stale `last_used_at` may get before a successful authentication rewrites it.
 * A key serving continuous traffic therefore costs one write a day, not one per
 * request. The column keeps full precision, so this can be narrowed later.
 */
export const API_KEY_LAST_USED_REFRESH_HOURS = 24

/**
 * Idleness that takes a key out of service. Because `last_used_at` lags real use by
 * up to the refresh window above, a key blocks between 30 and 31 days of true
 * idleness rather than exactly 30.
 */
export const API_KEY_IDLE_BLOCK_DAYS = 30

/**
 * Leading segment of a key, and of the `key_prefix` shown in listings.
 *
 * Non-prod collapses to one label: a personal stage is named after its developer,
 * which would put a dot inside a pasteable credential and a person's name in the UI.
 * The segment answers the only question a reader has — is this key dangerous.
 */
export const API_KEY_PREFIX_PROD = 'atlas_prod'
export const API_KEY_PREFIX_NONPROD = 'atlas_dev'

/** Enough to tell a user's keys apart in a list, far too few to narrow a search. */
export const API_KEY_PREFIX_SAMPLE_CHARS = 4

/**
 * The header a key travels in.
 *
 * NOT `Authorization`: a long-lived secret there can be taken for a Bearer token by
 * a proxy or a log scrubber, and a client holding one of two credential kinds would
 * have to guess which. Distinct from the retired `x-atlas-api-key` so a stale caller
 * gets a clean rejection rather than a partial success.
 */
export const API_KEY_HEADER = 'x-atlas-user-key'
